import { useState, useRef, useEffect, useCallback } from 'react';
import { db } from '../../services/firebase';
import { doc, updateDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { formatDateToKorean } from '../../utils';
import { API_BASE_URL, AI_API_URL } from '../../constants';
import { trackQAQuestion } from '../../utils';
import { useTranslation } from 'react-i18next';

export const useQA = (currentVideo) => {
  const { i18n } = useTranslation();
  const [qaInput, setQaInput] = useState("");
  const [qaLoading, setQaLoading] = useState(false);
  const [qaHistory, setQaHistory] = useState([]);
  const [answerLoadingStates, setAnswerLoadingStates] = useState([]);
  const [recommendedQuestions, setRecommendedQuestions] = useState([]);
  const qaScrollRef = useRef(null);

  const cleanQuestion = (q) =>
    q.replace(/^(\s*([\d]+[.)]|[a-zA-Z][.)]|[•\-–—●◆▶]|[①-⑳])\s*)+/g, '').trim();

  const handleQaSend = async (question) => {
    if (!currentVideo) return;

    const cleanedQuestion = cleanQuestion(question);
    setQaHistory(prev => [
      ...prev,
      { question: cleanedQuestion, answer: null }
    ]);
    setAnswerLoadingStates(prev => [...prev, true]);

    try {
      setQaLoading(true);
      const requestData = {
        question: cleanedQuestion,
        transcript: currentVideo.transcript || currentVideo.subtitle,
        summary: currentVideo.summary || "",
        mindmap: currentVideo.mindmap || "",
        language: i18n.language,
        languageName: i18n.language === 'ko' ? '한국어' : 
                      i18n.language === 'en' ? 'English' :
                      i18n.language === 'ja' ? '日本語' :
                      i18n.language === 'zh' ? '中文' :
                      i18n.language === 'es' ? 'Español' :
                      i18n.language === 'fr' ? 'Français' :
                      i18n.language === 'de' ? 'Deutsch' :
                      i18n.language === 'hi' ? 'हिंदी' : 'English'
      };
      
      console.log('Q&A 요청 데이터:', requestData);
      console.log('🌐 [Q&A] 타겟 언어:', requestData.language, '-', requestData.languageName);
      
      const response = await fetch(`${AI_API_URL}/api/qa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Q&A API 에러 응답:', response.status, errorText);
        throw new Error(`Failed to get answer: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      const now = new Date();

      const videoDocRef = doc(db, "users", currentVideo.userId, "history", currentVideo.id);
      const snap = await getDoc(videoDocRef);
      const data = snap.data();
      const prevQa = data && Array.isArray(data.qa) ? data.qa : [];
      const newQa = {
        question: cleanedQuestion,
        answer: result.answer
      };
      const updatedQa = [...prevQa, newQa];
      try {
        await updateDoc(videoDocRef, {
          qa: updatedQa,
          lastUpdated: serverTimestamp()
        });
        console.log('Firestore updateDoc(qa) 성공:', updatedQa);
      } catch (e) {
        console.error('Firestore updateDoc(qa) 실패:', e);
        alert('답변 저장에 실패했습니다. Firestore 권한 또는 네트워크를 확인하세요.');
      }

      setQaHistory(prev => {
        const newArr = [...prev];
        for (let i = newArr.length - 1; i >= 0; i--) {
          if (newArr[i].answer === null && newArr[i].question === cleanedQuestion) {
            newArr[i] = { ...newArr[i], answer: result.answer };
            break;
          }
        }
        return newArr;
      });
      setAnswerLoadingStates(prev => {
        const newArr = [...prev];
        for (let i = newArr.length - 1; i >= 0; i--) {
          if (newArr[i]) {
            newArr[i] = false;
            break;
          }
        }
        return newArr;
      });

      if (result.suggested_questions && result.suggested_questions.length > 0) {
        const cleanedQuestions = result.suggested_questions.map(q => cleanQuestion(q));
        try {
          await updateDoc(videoDocRef, {
            questions: cleanedQuestions
          });
          setRecommendedQuestions(cleanedQuestions);
        } catch (e) {
          console.error('Firestore updateDoc(questions) 실패:', e);
        }
      }

      trackQAQuestion(cleanedQuestion, currentVideo.title || currentVideo.youtubeTitle);

      return result;
    } catch (error) {
      console.error('Error in Q&A:', error);
      alert('답변 생성 또는 저장 중 오류가 발생했습니다.');
      setQaHistory(prev => prev.slice(0, -1));
      setAnswerLoadingStates(prev => prev.slice(0, -1));
      throw error;
    } finally {
      setQaLoading(false);
      setQaInput('');
    }
  };

  const handleRecommendedQuestionClick = async (q) => {
    if (qaLoading || !q) return;
    const remainingQuestions = recommendedQuestions.filter(qq => cleanQuestion(qq) !== cleanQuestion(q));
    setRecommendedQuestions(remainingQuestions);
    setQaLoading(true);
    await handleQaSend(cleanQuestion(q));
    setQaLoading(false);
  };

  useEffect(() => {
    if (currentVideo) {
      setQaHistory(currentVideo.qa ? [...currentVideo.qa] : []);
      setAnswerLoadingStates(currentVideo.qa ? currentVideo.qa.map(() => false) : []);
      setRecommendedQuestions(
        (currentVideo.questions || []).map(q => cleanQuestion(q))
      );
    } else {
      setQaHistory([]);
      setAnswerLoadingStates([]);
      setRecommendedQuestions([]);
    }
  }, [currentVideo?.id, currentVideo?.qa, currentVideo?.questions]);

  return {
    qaInput,
    setQaInput,
    qaLoading,
    qaHistory,
    answerLoadingStates,
    recommendedQuestions,
    handleQaSend,
    handleRecommendedQuestionClick,
    qaScrollRef
  };
}; 